import { useSyncExternalStore } from 'react'
import { analytics } from '../analytics/ConsoleAnalyticsAdapter'
import { voiceDiagnostics } from '../debug/VoiceDiagnosticStore'
import type { FareEstimate } from '../fares/FareCalculator'
import type {
  Coordinates,
  Destination,
  Language,
  MobilityProviderId,
  ProviderPosition,
  ProviderVariant,
} from '../types'
import { getDeviceInfo } from '../utils/device'
import type { Translator } from '../i18n/i18n'

interface DebugPanelProps {
  open: boolean
  language: Language
  pickup: Coordinates | null
  destination: Destination | null
  fare: FareEstimate | null
  providerVariant: ProviderVariant
  kridePosition: ProviderPosition
  uberPosition: ProviderPosition
  krideUrl: string | null
  kridePrefillSupported: boolean
  uberUrl: string | null
  uberError: string | null
  krideEnabled: boolean
  uberEnabled: boolean
  locationAvailable: boolean
  comparisonEnabled: boolean
  lastProviderClick: MobilityProviderId | null
  voiceDebugEnabled: boolean
  t: Translator
}

export function DebugPanel({
  open,
  language,
  pickup,
  destination,
  fare,
  providerVariant,
  kridePosition,
  uberPosition,
  krideUrl,
  kridePrefillSupported,
  uberUrl,
  uberError,
  krideEnabled,
  uberEnabled,
  locationAvailable,
  comparisonEnabled,
  lastProviderClick,
  voiceDebugEnabled,
  t,
}: DebugPanelProps) {
  const events = useSyncExternalStore(
    (listener) => analytics.subscribe(listener),
    () => analytics.getRecent(),
    () => [],
  )
  const voiceEvents = useSyncExternalStore(
    (listener) => voiceDiagnostics.subscribe(listener),
    () => voiceDiagnostics.getRecent(),
    () => [],
  )
  if (!open) return null
  const device = getDeviceInfo()
  const lastEvent = events.at(-1)
  const estimatedFare = fare ? `${fare.minWon.toLocaleString()}~${fare.maxWon.toLocaleString()} KRW` : 'NOT_AVAILABLE'

  return (
    <aside className="debug-panel" aria-label="Technical debug information">
      <h2>{t('debugPanel')}</h2>
      <dl>
        <dt>Language</dt><dd>{language}</dd>
        <dt>OS</dt><dd>{device.os}</dd>
        <dt>Browser</dt><dd>{device.browser}</dd>
        <dt>Destination</dt><dd>{destination?.name ?? 'NOT_SELECTED'}</dd>
        <dt>Destination Verification Status</dt><dd>{destination?.coordinateStatus ?? 'NOT_SELECTED'}</dd>
        <dt>Estimated Fare</dt><dd>{estimatedFare}</dd>
        <dt>Provider Variant</dt><dd>{providerVariant}</dd>
        <dt>K-RIDE Position</dt><dd>{kridePosition}</dd>
        <dt>Uber Position</dt><dd>{uberPosition}</dd>
        <dt>K-RIDE Enabled</dt><dd>{String(krideEnabled)}</dd>
        <dt>Uber Enabled</dt><dd>{String(uberEnabled)}</dd>
        <dt>Location Available</dt><dd>{String(locationAvailable)}</dd>
        <dt>Provider Compare Ready</dt><dd>{String(comparisonEnabled)}</dd>
        <dt>K-RIDE App Start URL</dt><dd className="url-value">{krideUrl ?? 'NOT_READY'}</dd>
        <dt>K-RIDE Destination Prefill Support</dt><dd>{kridePrefillSupported ? 'VERIFIED' : 'NOT VERIFIED / NOT AVAILABLE'}</dd>
        <dt>Uber Generated URL</dt><dd className="url-value">{uberUrl ?? uberError ?? 'NOT_READY'}</dd>
        <dt>Uber Deep Link</dt><dd>{uberUrl ? 'CODE READY' : 'NOT_READY'}</dd>
        <dt>Uber Korea Device Test Status</dt><dd>NEEDS DEVICE TEST</dd>
        <dt>Last Provider Click</dt><dd>{lastProviderClick ?? 'NONE'}</dd>
        <dt>Last Analytics Event</dt><dd>{lastEvent?.name ?? 'NONE'}</dd>
        <dt>Voice/STT Debug Mode</dt><dd>{voiceDebugEnabled ? 'ENABLED (?debug=1)' : 'DISABLED'}</dd>
        <dt>pickup latitude</dt><dd>{pickup?.latitude ?? 'NOT_AVAILABLE'}</dd>
        <dt>pickup longitude</dt><dd>{pickup?.longitude ?? 'NOT_AVAILABLE'}</dd>
        <dt>dropoff latitude</dt><dd>{destination?.latitude ?? 'NOT_SELECTED'}</dd>
        <dt>dropoff longitude</dt><dd>{destination?.longitude ?? 'NOT_SELECTED'}</dd>
      </dl>
      <h3>Voice/STT diagnostics</h3>
      {voiceEvents.length === 0 ? (
        <p>{voiceDebugEnabled ? 'NO VOICE ATTEMPT YET' : 'ADD ?debug=1 TO THE URL'}</p>
      ) : (
        <ol className="event-log voice-diagnostic-log">
          {voiceEvents.slice(-20).reverse().map((event, index) => (
            <li key={`${event.timestamp}-${event.source}-${event.stage}-${index}`}>
              <time>{event.timestamp.slice(11, 19)}</time>{' '}
              <strong>[{event.source}] {event.stage}</strong>{' '}
              <span>{JSON.stringify(event.details)}</span>
            </li>
          ))}
        </ol>
      )}
      <h3>{t('recentAnalytics')}</h3>
      <ol className="event-log">
        {events.slice(-12).reverse().map((event) => (
          <li key={`${event.timestamp}-${event.name}`}><time>{event.timestamp.slice(11, 19)}</time> {event.name}</li>
        ))}
      </ol>
    </aside>
  )
}
